/**
 * Trunnion AI - Agent Configuration Form
 * Agent configuration within the node inspector.
 */

import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { ClearanceSelector } from './ClearanceSelector';
import { ToolSelector } from './ToolSelector';
import { useAgentList } from '@/hooks/queries/useAgents';
import { useWorkflowDesignerStore } from '@/stores/workflowDesignerStore';
import type { AgentNodeData, ToolWhitelistEntry } from '@/types';

interface AgentConfigFormProps {
  nodeId: string;
  data: AgentNodeData;
}

export function AgentConfigForm({ nodeId, data }: AgentConfigFormProps) {
  const updateNodeData = useWorkflowDesignerStore((s) => s.updateNodeData);
  const { data: agentsData } = useAgentList({});

  const agents = agentsData?.agents ?? [];

  const update = (updates: Partial<AgentNodeData>) => {
    updateNodeData(nodeId, updates);
  };

  const handleAgentSelect = (agentId: string) => {
    const agent = agents.find((a) => a.id === agentId);
    if (agent) {
      update({
        agentId: agent.id,
        agentName: agent.name,
        tier: agent.tier,
        mode: agent.mode,
        classification: agent.maxClassification,
        toolCount: agent.toolWhitelist.length,
      });
    }
  };

  return (
    <div className="space-y-4">
      <Input
        label="Node Label"
        value={data.label}
        onChange={(e) => update({ label: e.target.value })}
      />

      <Select
        label="Agent"
        value={data.agentId}
        onChange={(e) => handleAgentSelect(e.target.value)}
        options={agents.map((a) => ({ value: a.id, label: a.name }))}
        placeholder="Select an agent..."
      />

      <Select
        label="Mode"
        value={data.mode}
        onChange={(e) =>
          update({ mode: e.target.value as AgentNodeData['mode'] })
        }
        options={[
          { value: 'autonomous', label: 'Autonomous' },
          { value: 'supervised', label: 'Supervised' },
          { value: 'manual', label: 'Manual' },
        ]}
      />

      <Select
        label="Tier"
        value={data.tier}
        onChange={(e) =>
          update({ tier: e.target.value as AgentNodeData['tier'] })
        }
        options={[
          { value: 'orchestrator', label: 'Orchestrator' },
          { value: 'specialist', label: 'Specialist' },
          { value: 'utility', label: 'Utility' },
        ]}
      />

      <ClearanceSelector
        value={data.classification}
        onChange={(classification) => update({ classification })}
      />

      <Input
        label="Timeout (seconds)"
        type="number"
        value={data.timeoutSeconds}
        onChange={(e) => update({ timeoutSeconds: parseInt(e.target.value) || 300 })}
      />

      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Max Retries"
          type="number"
          value={data.retryPolicy.maxRetries}
          onChange={(e) =>
            update({
              retryPolicy: {
                ...data.retryPolicy,
                maxRetries: parseInt(e.target.value) || 0,
              },
            })
          }
        />
        <Input
          label="Backoff Multiplier"
          type="number"
          value={data.retryPolicy.backoffMultiplier}
          onChange={(e) =>
            update({
              retryPolicy: {
                ...data.retryPolicy,
                backoffMultiplier: parseFloat(e.target.value) || 1,
              },
            })
          }
        />
      </div>

      <ToolSelector
        selectedTools={[]}
        onChange={(tools: ToolWhitelistEntry[]) =>
          update({ toolCount: tools.length })
        }
      />

      <div>
        <label className="text-sm font-medium text-trunnion-slate-200">
          System Prompt Override
        </label>
        <textarea
          value={data.systemPromptOverride ?? ''}
          onChange={(e) => update({ systemPromptOverride: e.target.value })}
          rows={4}
          placeholder="Optional: override the agent's system prompt for this node..."
          className="mt-1.5 w-full rounded-lg border border-trunnion-slate-600 bg-trunnion-slate-800 px-3 py-2 text-sm text-white placeholder-trunnion-slate-400 focus:outline-none focus:ring-2 focus:ring-trunnion-gold-500 resize-none"
        />
      </div>
    </div>
  );
}
